import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight, Plus, Pencil, Trash2, Eye, Download, ArrowLeft, ArrowRight, Loader2, Search, Send, MessageCircle, UserCog, Key } from 'lucide-react';
import KianNewsAdmin from '@/components/admin/KianNewsAdmin';
import NewsletterAdmin from '@/components/admin/NewsletterAdmin';
import { API, AuthContext, useAuth, adminApi, ic, LoginPage, AdminLayout } from '@/components/admin/AdminShell';
import { CrudPage } from '@/components/admin/CrudForm';
import MenuBuilder from '@/pages/admin/MenuBuilder';
import { hwCategories, productFields, solutionFields, caseFields, crewFields, hwCategoryFields, releaseFields } from '@/components/admin/fields';

// Note: keep this line so existing references to `API` inside this file still work
// (it's re-exported from AdminShell.js — import above).


// ===== DASHBOARD =====
const DashboardPage = () => {
  const auth = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { adminApi(auth.token).get('/admin/stats').then(r=>setStats(r.data)).catch(()=>{}); }, [auth.token]);
  const cards = stats ? [
    { label:'Leads totales', value:stats.leads_total, color:'bg-sky-50 text-[#0EA5E9]' },
    { label:'Leads nuevos', value:stats.leads_new, color:'bg-emerald-50 text-emerald-600' },
    { label:'Productos', value:stats.products, color:'bg-violet-50 text-violet-600' },
    { label:'Soluciones', value:stats.solutions, color:'bg-amber-50 text-amber-600' },
    { label:'Casos', value:stats.cases, color:'bg-rose-50 text-rose-600' },
    { label:'Contactos', value:stats.contacts, color:'bg-teal-50 text-teal-600' },
  ] : [];
  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-semibold mb-6" style={{fontFamily:'Syne,sans-serif'}}>Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">{cards.map((c,i)=>(<div key={i} className={`p-5 rounded-xl border border-slate-100 ${c.color}`}><div className="text-3xl font-bold" style={{fontFamily:'Syne,sans-serif'}}>{c.value}</div><div className="text-sm mt-1 opacity-80">{c.label}</div></div>))}</div>
      <div className="p-5 rounded-xl bg-white border border-slate-200"><h3 className="text-sm font-semibold mb-3">Accesos rápidos</h3><div className="space-y-2">
        <Link to="/admin/leads" className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50"><span className="text-sm">Ver leads</span><ChevronRight className="w-4 h-4 text-slate-400" /></Link>
        <Link to="/admin/products" className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50"><span className="text-sm">Gestionar productos</span><ChevronRight className="w-4 h-4 text-slate-400" /></Link>
        <Link to="/admin/solutions" className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50"><span className="text-sm">Gestionar soluciones</span><ChevronRight className="w-4 h-4 text-slate-400" /></Link>
        <Link to="/admin/cases" className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50"><span className="text-sm">Gestionar casos</span><ChevronRight className="w-4 h-4 text-slate-400" /></Link>
        <Link to="/" target="_blank" className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50"><span className="text-sm">Ver sitio público</span><Eye className="w-4 h-4 text-slate-400" /></Link>
      </div></div>
    </div>
  );
};

// ===== LEADS =====
const LeadsPage = () => {
  const auth = useAuth();
  const [leads, setLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [showExport, setShowExport] = useState(false);

  const fetchLeads = useCallback(() => { setLoading(true); Promise.all([adminApi(auth.token).get('/admin/leads'), adminApi(auth.token).get('/admin/team-members')]).then(([lr,tr])=>{setLeads(lr.data);setTeamMembers(tr.data);setLoading(false);}).catch(()=>setLoading(false)); }, [auth.token]);
  useEffect(fetchLeads, [fetchLeads]);

  const updateLead = async (id, data) => { await adminApi(auth.token).put(`/admin/leads/${id}`, data); toast.success('Lead actualizado'); fetchLeads(); };

  const formatDate = (d) => { if(!d) return '-'; const dt=new Date(d); return dt.toLocaleDateString('es-CL',{day:'2-digit',month:'2-digit',year:'numeric'}); };
  const formatTime = (d) => { if(!d) return ''; const dt=new Date(d); return dt.toLocaleTimeString('es-CL',{hour:'2-digit',minute:'2-digit'}); };
  const truncate = (s, n=35) => s && s.length > n ? s.slice(0,n)+'...' : s;

  // Filtering
  let filtered = leads.filter(l => {
    if (filter && !JSON.stringify(l).toLowerCase().includes(filter.toLowerCase())) return false;
    if (statusFilter && l.status !== statusFilter) return false;
    if (typeFilter && l.form_type !== typeFilter) return false;
    if (assigneeFilter && (l.assigned_to || '') !== assigneeFilter) return false;
    return true;
  });

  // Sorting
  filtered.sort((a, b) => {
    let va = a[sortField] || '', vb = b[sortField] || '';
    if (sortField === 'created_at') { va = new Date(va || 0).getTime(); vb = new Date(vb || 0).getTime(); }
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => { if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(field); setSortDir('desc'); } };
  const SortIcon = ({ field }) => sortField === field ? <span className="text-[#0EA5E9]">{sortDir === 'asc' ? '↑' : '↓'}</span> : <span className="text-slate-300">↕</span>;

  // Export to Excel
  const exportExcel = async () => {
    const XLSX = await import('xlsx');
    let data = leads;
    if (exportFrom || exportTo) {
      data = leads.filter(l => {
        if (!l.created_at) return false;
        if (exportFrom && l.created_at < exportFrom) return false;
        if (exportTo && l.created_at > exportTo + 'T23:59:59') return false;
        return true;
      });
    }
    const rows = data.map(l => ({
      'Nombre': l.name || '',
      'Email Remitente': l.email || '',
      'Teléfono': l.phone || '',
      'Empresa': l.company || '',
      'Industria': l.industry || '',
      'Interés': l.interest || '',
      'Modelo': l.model_type || '',
      'Tipo Formulario': l.form_type || '',
      'Mensaje': l.message || '',
      'Estado': l.status || '',
      'Responsable': l.assigned_to || '',
      'Email Enviado': l.email_sent ? 'Sí' : 'No',
      'Destino ALLCOM': l.email_destination || '',
      'Página Origen': l.source_page || '',
      'Fecha': formatDate(l.created_at),
      'Hora': formatTime(l.created_at),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    // Style: column widths
    ws['!cols'] = [{ wch: 20 },{ wch: 28 },{ wch: 15 },{ wch: 20 },{ wch: 14 },{ wch: 16 },{ wch: 12 },{ wch: 14 },{ wch: 40 },{ wch: 12 },{ wch: 18 },{ wch: 12 },{ wch: 22 },{ wch: 30 },{ wch: 12 },{ wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads ALLCOM');
    XLSX.writeFile(wb, `leads_allcom_${new Date().toISOString().slice(0,10)}.xlsx`);
    setShowExport(false);
    toast.success(`Exportados ${rows.length} leads a Excel`);
  };

  const sc = {new:'bg-sky-100 text-sky-700',contacted:'bg-amber-100 text-amber-700',qualified:'bg-emerald-100 text-emerald-700',closed:'bg-slate-100 text-slate-500'};
  const uniqueTypes = [...new Set(leads.map(l => l.form_type).filter(Boolean))];

  return (
    <div data-testid="admin-leads">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold" style={{fontFamily:'Syne,sans-serif'}}>Leads ({filtered.length})</h1>
        <button onClick={()=>setShowExport(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50"><Download className="w-4 h-4" />Exportar Excel</button>
      </div>

      {/* Filters bar */}
      <div className="flex flex-wrap gap-3 mb-4 p-3 bg-white rounded-xl border border-slate-200">
        <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Buscar..." className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-[#0EA5E9]/30" /></div>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white">
          <option value="">Todos los estados</option><option value="new">Nuevo</option><option value="contacted">Contactado</option><option value="qualified">Calificado</option><option value="closed">Cerrado</option>
        </select>
        <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} className="px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white">
          <option value="">Todos los tipos</option>{uniqueTypes.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <select value={assigneeFilter} onChange={e=>setAssigneeFilter(e.target.value)} className="px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white">
          <option value="">Todos los responsables</option><option value="">Sin asignar</option>{teamMembers.map(m=><option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
      </div>

      {loading?<Loader2 className="w-6 h-6 animate-spin text-slate-400" />:(
        <div className="bg-white rounded-xl border border-slate-200" style={{maxHeight:'65vh',overflow:'auto'}}>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-3 font-medium text-slate-500 text-xs cursor-pointer hover:text-[#0EA5E9]" onClick={()=>toggleSort('name')}>Nombre <SortIcon field="name" /></th>
                <th className="text-left px-3 py-3 font-medium text-slate-500 text-xs cursor-pointer hover:text-[#0EA5E9]" onClick={()=>toggleSort('email')}>Desde <SortIcon field="email" /></th>
                <th className="text-left px-3 py-3 font-medium text-slate-500 text-xs">Para (ALLCOM)</th>
                <th className="text-left px-3 py-3 font-medium text-slate-500 text-xs">Mensaje</th>
                <th className="text-left px-3 py-3 font-medium text-slate-500 text-xs cursor-pointer hover:text-[#0EA5E9]" onClick={()=>toggleSort('assigned_to')}>Responsable <SortIcon field="assigned_to" /></th>
                <th className="text-left px-3 py-3 font-medium text-slate-500 text-xs cursor-pointer hover:text-[#0EA5E9]" onClick={()=>toggleSort('status')}>Estado <SortIcon field="status" /></th>
                <th className="text-left px-3 py-3 font-medium text-slate-500 text-xs cursor-pointer hover:text-[#0EA5E9]" onClick={()=>toggleSort('created_at')}>Fecha <SortIcon field="created_at" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(l=>(
                <tr key={l.id} className="hover:bg-slate-50 cursor-pointer" onClick={()=>setSelectedLead(l)}>
                  <td className="px-3 py-2.5">
                    <div className="text-sm font-medium text-slate-900">{l.name}</div>
                    {l.company && <div className="text-[10px] text-slate-400">{l.company}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="text-xs text-[#0EA5E9] font-medium">{l.email}</div>
                    {l.phone && <div className="text-[10px] text-slate-400">{l.phone}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${l.email_sent ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{l.email_sent ? '✓' : '✗'}</span>
                    <div className="text-[10px] text-slate-400 mt-0.5">{l.email_destination || '-'}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    {l.message ? (
                      <div className="group relative"><span className="text-xs text-slate-600">{truncate(l.message)}</span>
                        {l.message.length > 35 && <div className="absolute z-50 hidden group-hover:block bottom-full left-0 mb-2 w-72 p-3 bg-white rounded-xl border border-slate-200 shadow-xl text-xs text-slate-700 leading-relaxed"><div className="text-[10px] font-semibold text-slate-400 mb-1">Mensaje:</div>{l.message}</div>}
                      </div>
                    ) : <span className="text-[10px] text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={l.assigned_to||''} onClick={e=>e.stopPropagation()} onChange={e=>{e.stopPropagation();updateLead(l.id,{assigned_to:e.target.value})}} className="text-[11px] px-2 py-1 rounded-lg border border-slate-200 outline-none cursor-pointer bg-white w-full max-w-[120px]">
                      <option value="">Sin asignar</option>{teamMembers.map(m=><option key={m.id} value={m.name}>{m.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={l.status} onClick={e=>e.stopPropagation()} onChange={e=>{e.stopPropagation();updateLead(l.id,{status:e.target.value})}} className={`text-[11px] px-2 py-1 rounded-full font-medium outline-none cursor-pointer ${sc[l.status]||'bg-slate-100'}`}>
                      <option value="new">Nuevo</option><option value="contacted">Contactado</option><option value="qualified">Calificado</option><option value="closed">Cerrado</option>
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="text-xs text-slate-600">{formatDate(l.created_at)}</div>
                    <div className="text-[10px] text-slate-400">{formatTime(l.created_at)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length===0&&<div className="p-8 text-center text-slate-400">No hay leads con estos filtros</div>}
        </div>
      )}

      {/* Export Modal */}
      {showExport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={()=>setShowExport(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-6" onClick={e=>e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-4" style={{fontFamily:'Syne'}}>Exportar a Excel</h3>
            <div className="space-y-3 mb-5">
              <div><label className="text-xs font-medium text-slate-500 mb-1 block">Desde</label><input type="date" value={exportFrom} onChange={e=>setExportFrom(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              <div><label className="text-xs font-medium text-slate-500 mb-1 block">Hasta</label><input type="date" value={exportTo} onChange={e=>setExportTo(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              <p className="text-[10px] text-slate-400">Dejar vacío para exportar todos los leads.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={exportExcel} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600">Descargar .xlsx</button>
              <button onClick={()=>setShowExport(false)} className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Popup */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={()=>setSelectedLead(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold" style={{fontFamily:'Syne'}}>Detalle del Lead</h3>
              <button onClick={()=>setSelectedLead(null)} className="text-slate-400 hover:text-slate-600 text-lg font-bold">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Nombre</div><div className="text-sm font-semibold">{selectedLead.name}</div></div>
                <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Empresa</div><div className="text-sm">{selectedLead.company||'-'}</div></div>
                <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Email remitente</div><div className="text-sm text-[#0EA5E9] font-medium">{selectedLead.email}</div></div>
                <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Teléfono</div><div className="text-sm">{selectedLead.phone||'-'}</div></div>
                <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Industria</div><div className="text-sm">{selectedLead.industry||'-'}</div></div>
                <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Interés</div><div className="text-sm">{selectedLead.interest||'-'}</div></div>
              </div>
              <hr className="border-slate-100" />
              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Enviado a ALLCOM</div><div className="text-sm font-medium">{selectedLead.email_destination||'-'}</div><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold inline-block mt-1 ${selectedLead.email_sent ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{selectedLead.email_sent ? '✓ Email enviado' : '✗ Email no enviado'}</span></div>
                <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Fecha y hora</div><div className="text-sm">{formatDate(selectedLead.created_at)}</div><div className="text-xs text-slate-400">{formatTime(selectedLead.created_at)} hrs</div></div>
              </div>
              <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Responsable</div><div className="text-sm font-medium">{selectedLead.assigned_to || 'Sin asignar'}</div></div>
              <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Tipo / Origen</div><div className="flex gap-2"><span className="text-[10px] px-2 py-0.5 bg-slate-100 rounded-full font-medium">{selectedLead.form_type}</span><span className="text-[10px] text-slate-400">{selectedLead.source_page}</span></div></div>
              {selectedLead.message && <div><div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Mensaje</div><div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-sm text-slate-700 leading-relaxed">{selectedLead.message}</div></div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ===== TEAM MEMBERS PAGE =====
const TeamPage = () => {
  const auth = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const fetchMembers = useCallback(() => { setLoading(true); adminApi(auth.token).get('/admin/team-members').then(r=>{setMembers(r.data||[]);setLoading(false);}).catch(()=>setLoading(false)); }, [auth.token]);
  useEffect(fetchMembers, [fetchMembers]);
  const deleteMember = async (id) => { if(!window.confirm('¿Eliminar?')) return; await adminApi(auth.token).delete(`/admin/team-members/${id}`); toast.success('Eliminado'); fetchMembers(); };
  const saveMember = async (data) => {
    try {
      if(data.id) await adminApi(auth.token).put(`/admin/team-members/${data.id}`,data);
      else await adminApi(auth.token).post('/admin/team-members',data);
      toast.success('Guardado'); setEditing(null); fetchMembers();
    } catch { toast.error('Error al guardar'); }
  };

  if (editing !== null) return (
    <div>
      <button onClick={()=>setEditing(null)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft className="w-4 h-4" />Volver</button>
      <h2 className="text-xl font-semibold mb-6" style={{fontFamily:'Syne'}}>{editing.id ? 'Editar' : 'Nuevo'} miembro</h2>
      <form onSubmit={e=>{e.preventDefault(); saveMember(editing);}} className="max-w-lg space-y-4 bg-white p-6 rounded-xl border border-slate-200">
        <input placeholder="Nombre completo" value={editing.name||''} onChange={e=>setEditing({...editing,name:e.target.value})} required className={ic} />
        <input placeholder="Email" value={editing.email||''} onChange={e=>setEditing({...editing,email:e.target.value})} className={ic} />
        <input placeholder="Cargo / Rol" value={editing.role||''} onChange={e=>setEditing({...editing,role:e.target.value})} className={ic} />
        <input placeholder="Teléfono" value={editing.phone||''} onChange={e=>setEditing({...editing,phone:e.target.value})} className={ic} />
        <button type="submit" className="px-6 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600">Guardar</button>
      </form>
    </div>
  );

  return (
    <div data-testid="admin-team">
      <div className="flex items-center justify-between mb-6"><h1 className="text-2xl font-semibold" style={{fontFamily:'Syne'}}>Equipo / Responsables ({members.length})</h1><button onClick={()=>setEditing({})} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600"><Plus className="w-4 h-4" />Nuevo miembro</button></div>
      <p className="text-xs text-slate-400 mb-4">Personas que pueden ser asignadas como responsables de un lead.</p>
      {loading?<Loader2 className="w-6 h-6 animate-spin" />:(
        <div className="space-y-2">
          {members.map(m=>(<div key={m.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
            <div><h3 className="text-sm font-semibold">{m.name}</h3><div className="text-xs text-slate-500">{m.role||''} {m.email ? `· ${m.email}` : ''} {m.phone ? `· ${m.phone}` : ''}</div></div>
            <div className="flex gap-1"><button onClick={()=>setEditing(m)} className="p-2 text-slate-400 hover:text-[#0EA5E9] hover:bg-sky-50 rounded-lg"><Pencil className="w-4 h-4" /></button><button onClick={()=>deleteMember(m.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button></div>
          </div>))}
          {members.length===0&&<div className="p-8 text-center text-slate-400 bg-white rounded-xl border border-slate-200">No hay miembros. Agrega el primero para poder asignar leads.</div>}
        </div>
      )}
    </div>
  );
};
// ===== FIELD DEFINITIONS =====
// ===== SITE CONFIG PAGE =====
const SiteConfigPage = () => {
  const auth = useAuth();
  const [config, setConfig] = useState({
    id: 'landing',
    hero_video: '/hero-video.mp4',
    hero_badge: 'Soluciones inteligentes para empresas que no se detienen',
    hero_h1: 'El futuro del autoservicio y el Retail está aquí.',
    hero_subtitle: 'Bienvenido a la tecnología con plataformas inteligentes, integración y operación 24/7 para negocios y organizaciones exigentes.',
    kpi_title: 'Cifras que nos respaldan',
    platform_title: 'Plataforma ALLCOM',
    solutions_title: 'Soluciones integrales para tu industria',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!auth?.token) { setLoading(false); return; }
    adminApi(auth.token).get('/admin/site-config')
      .then(r => { if (r.data) setConfig(prev => ({ ...prev, ...r.data })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [auth?.token]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi(auth.token).put('/admin/site-config', config);
      toast.success('Configuración guardada correctamente');
    } catch (err) {
      toast.error('Error al guardar la configuración');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /><span className="text-sm text-slate-400">Cargando configuración...</span></div>;

  return (
    <div data-testid="admin-site-config">
      <h1 className="text-2xl font-semibold mb-2" style={{fontFamily:'Syne,sans-serif'}}>Configuración Landing / Hero</h1>
      <p className="text-sm text-slate-400 mb-6">Modifica los textos principales del landing, el video del hero y los títulos de sección.</p>
      <div className="max-w-2xl space-y-5 bg-white p-6 rounded-xl border border-slate-200">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Video Hero (URL o ruta local)</label>
          <input value={config.hero_video || ''} onChange={e => setConfig({...config, hero_video: e.target.value})} className={ic} placeholder="/hero-video.mp4" />
          <p className="text-[10px] text-slate-400 mt-1">Ruta del video. Ej: /hero-video.mp4 o URL externa.</p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Badge (burbuja superior del hero)</label>
          <input value={config.hero_badge || ''} onChange={e => setConfig({...config, hero_badge: e.target.value})} className={ic} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">H1 (título principal)</label>
          <input value={config.hero_h1 || ''} onChange={e => setConfig({...config, hero_h1: e.target.value})} className={ic} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Subtítulo del hero</label>
          <textarea value={config.hero_subtitle || ''} onChange={e => setConfig({...config, hero_subtitle: e.target.value})} rows={3} className={ic} />
        </div>
        <hr className="border-slate-100" />
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Título sección KPIs</label>
          <input value={config.kpi_title || ''} onChange={e => setConfig({...config, kpi_title: e.target.value})} className={ic} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Título sección Plataforma</label>
          <input value={config.platform_title || ''} onChange={e => setConfig({...config, platform_title: e.target.value})} className={ic} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Título sección Soluciones</label>
          <input value={config.solutions_title || ''} onChange={e => setConfig({...config, solutions_title: e.target.value})} className={ic} />
        </div>
        <div className="pt-2">
          <button onClick={save} disabled={saving} className="px-6 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 disabled:opacity-50 transition-colors">
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};




// ===== CONTENT MANAGER PAGE =====
const ContentManagerPage = () => {
  const auth = useAuth();
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load pages registry
  useEffect(() => {
    if (!auth?.token) return;
    adminApi(auth.token).get('/admin/pages').then(r => { setPages(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, [auth?.token]);

  // Load content for selected page
  const loadContent = async (page) => {
    setSelectedPage(page);
    setData(null);
    try {
      if (page.type === 'list') {
        const r = await adminApi(auth.token).get(`/admin/content-list/${page.id}`);
        setData(r.data?.items || []);
      } else {
        const r = await adminApi(auth.token).get(`/admin/content/${page.id}`);
        setData(r.data || []);
      }
    } catch { setData(page.type === 'list' ? [] : []); }
  };

  // Save list content
  const saveList = async (items) => {
    setSaving(true);
    try {
      await adminApi(auth.token).put(`/admin/content-list/${selectedPage.id}`, { items });
      toast.success('Guardado correctamente');
      setData(items);
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  // Save block content
  const saveBlock = async (key, value) => {
    setSaving(true);
    try {
      await adminApi(auth.token).put(`/admin/content/${selectedPage.id}/${key}`, { value });
      toast.success(`"${key}" actualizado`);
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /><span className="text-sm text-slate-400">Cargando...</span></div>;

  // Page selector
  if (!selectedPage) return (
    <div data-testid="admin-content">
      <h1 className="text-2xl font-semibold mb-2" style={{fontFamily:'Syne,sans-serif'}}>Contenido del Sitio</h1>
      <p className="text-sm text-slate-400 mb-6">Administra todos los textos, listas e imágenes del sitio sin necesidad de DEV.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pages.map(p => (
          <button key={p.id} onClick={() => loadContent(p)} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-[#0EA5E9]/30 transition-all text-left">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-slate-100 text-slate-500">{p.type}</span>
            </div>
            <h3 className="text-sm font-semibold text-[#0A2540]" style={{fontFamily:'Syne'}}>{p.label}</h3>
            <p className="text-xs text-[#64748B] mt-1">{p.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );

  // List editor
  if (selectedPage.type === 'list') return (
    <div>
      <button onClick={() => setSelectedPage(null)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft className="w-4 h-4" />Volver</button>
      <h1 className="text-2xl font-semibold mb-2" style={{fontFamily:'Syne'}}>{selectedPage.label}</h1>
      <p className="text-xs text-slate-400 mb-4">{selectedPage.desc}</p>
      <ListEditor items={data || []} onSave={saveList} saving={saving} pageId={selectedPage.id} />
    </div>
  );

  // Block editor
  return (
    <div>
      <button onClick={() => setSelectedPage(null)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft className="w-4 h-4" />Volver</button>
      <h1 className="text-2xl font-semibold mb-2" style={{fontFamily:'Syne'}}>{selectedPage.label}</h1>
      <p className="text-xs text-slate-400 mb-4">{selectedPage.desc}</p>
      <BlockEditor blocks={data || []} onSave={saveBlock} saving={saving} pageId={selectedPage.id} />
    </div>
  );
};

// List editor (for partners, KPIs, industries, FAQ, etc.)
const ListEditor = ({ items, onSave, saving, pageId }) => {
  const [list, setList] = useState(items);
  const [editIdx, setEditIdx] = useState(null);
  const [editItem, setEditItem] = useState({});

  useEffect(() => { setList(items); }, [items]);

  const fields = {
    'landing-partners': ['name'],
    'landing-kpis': ['label', 'value', 'suffix'],
    'landing-industries': ['name', 'desc', 'icon', 'image'],
    'landing-solutions': ['title', 'desc', 'icon', 'href'],
    'faq-general': ['q', 'a'],
  };
  const cols = fields[pageId] || ['name', 'value'];

  const addItem = () => { setEditIdx(-1); setEditItem({}); };
  const saveItem = () => {
    const newList = [...list];
    if (editIdx === -1) newList.push(editItem);
    else newList[editIdx] = editItem;
    setList(newList);
    setEditIdx(null);
    onSave(newList);
  };
  const removeItem = (idx) => { const newList = list.filter((_, i) => i !== idx); setList(newList); onSave(newList); };
  const moveItem = (idx, dir) => {
    const newList = [...list];
    const target = idx + dir;
    if (target < 0 || target >= newList.length) return;
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    setList(newList);
    onSave(newList);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-slate-500">{list.length} items</span>
        <button onClick={addItem} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600"><Plus className="w-4 h-4" />Agregar</button>
      </div>
      {editIdx !== null && (
        <div className="mb-4 p-4 rounded-xl bg-white border border-[#0EA5E9]/30 shadow-sm space-y-3">
          {cols.map(col => (
            <div key={col}>
              <label className="text-xs font-medium text-slate-500 mb-1 block">{col}</label>
              {col === 'a' || col === 'desc' ? (
                <textarea value={editItem[col] || ''} onChange={e => setEditItem({...editItem, [col]: e.target.value})} rows={3} className={ic} />
              ) : (
                <input value={editItem[col] || ''} onChange={e => setEditItem({...editItem, [col]: e.target.value})} className={ic} />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={saveItem} className="px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600">Guardar</button>
            <button onClick={() => setEditIdx(null)} className="px-4 py-2 text-sm font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50">Cancelar</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {list.map((item, idx) => (
          <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200">
            <div className="flex flex-col gap-0.5">
              <button onClick={() => moveItem(idx, -1)} className="text-slate-300 hover:text-slate-500 text-[10px]">▲</button>
              <button onClick={() => moveItem(idx, 1)} className="text-slate-300 hover:text-slate-500 text-[10px]">▼</button>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium">{item.name || item.label || item.title || item.q || JSON.stringify(item).slice(0,60)}</span>
              {item.desc && <p className="text-xs text-slate-400 truncate">{item.desc}</p>}
              {item.value && <span className="text-xs text-[#0EA5E9] ml-2">{item.value}{item.suffix||''}</span>}
            </div>
            <button onClick={() => { setEditIdx(idx); setEditItem(item); }} className="p-1.5 text-slate-400 hover:text-[#0EA5E9] hover:bg-sky-50 rounded-lg"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={() => removeItem(idx)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {list.length === 0 && <div className="p-6 text-center text-slate-400 bg-white rounded-xl border border-slate-200 text-sm">No hay items. Los valores por defecto del código se usarán hasta que agregues contenido aquí.</div>}
      </div>
    </div>
  );
};

// Block editor (for text blocks like nosotros, historia, etc.)
const BlockEditor = ({ blocks, onSave, saving, pageId }) => {
  const [localBlocks, setLocalBlocks] = useState({});
  const [newKey, setNewKey] = useState('');

  useEffect(() => {
    const obj = {};
    blocks.forEach(b => { obj[b.key] = b.value; });
    setLocalBlocks(obj);
  }, [blocks]);

  const presets = {
    'landing-ceo': ['foto', 'nombre', 'titulo', 'quote_1', 'quote_2', 'quote_3', 'quote_4', 'goal_1_title', 'goal_1_desc', 'goal_2_title', 'goal_2_desc', 'goal_3_title', 'goal_3_desc', 'goal_4_title', 'goal_4_desc'],
    'landing-manifesto': ['titulo', 'bajada_1', 'bajada_2'],
    'landing-office': ['foto_url', 'titulo', 'direccion', 'subtexto'],
    'page-nosotros': ['hero_h1', 'hero_bajada', 'nosotros_p1', 'nosotros_p2', 'nosotros_p3', 'nosotros_p4', 'cierre'],
    'page-historia': ['hero_h1', 'hero_bajada', 'intro', 'cierre'],
    'page-ia-studio': ['hero_h1', 'hero_bajada', 'plan_1_precio', 'plan_2_precio', 'plan_3_precio'],
    'page-openclaw': ['hero_h1', 'hero_h2', 'hero_bajada_1', 'hero_bajada_2', 'plan_1_precio', 'plan_2_precio', 'plan_3_precio'],
    'footer': ['tagline', 'telefono', 'email_ventas', 'email_contacto', 'direccion', 'copyright'],
  };

  const keys = presets[pageId] || Object.keys(localBlocks);
  const allKeys = [...new Set([...keys, ...Object.keys(localBlocks)])];

  const saveField = (key) => { onSave(key, localBlocks[key] || ''); };
  const addField = () => { if (newKey) { setLocalBlocks({...localBlocks, [newKey]: ''}); setNewKey(''); } };

  return (
    <div className="max-w-2xl space-y-4">
      {allKeys.map(key => (
        <div key={key} className="bg-white p-4 rounded-xl border border-slate-200">
          <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">{key.replace(/_/g, ' ')}</label>
          {(key.includes('_p') || key.includes('intro') || key.includes('bajada') || key.includes('desc') || key.includes('cierre')) ? (
            <textarea value={localBlocks[key] || ''} onChange={e => setLocalBlocks({...localBlocks, [key]: e.target.value})} rows={4} className={ic} />
          ) : (
            <input value={localBlocks[key] || ''} onChange={e => setLocalBlocks({...localBlocks, [key]: e.target.value})} className={ic} />
          )}
          <button onClick={() => saveField(key)} disabled={saving} className="mt-2 px-3 py-1.5 text-xs font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600 disabled:opacity-50">
            Guardar
          </button>
        </div>
      ))}
      <div className="flex gap-2 mt-4">
        <input value={newKey} onChange={e => setNewKey(e.target.value)} placeholder="Nuevo campo (key)" className={ic + ' flex-1'} />
        <button onClick={addField} className="px-4 py-2 text-sm font-semibold text-white bg-slate-700 rounded-lg hover:bg-slate-800">+ Campo</button>
      </div>
    </div>
  );
};

// ===== WHATSAPP CONFIG PAGE =====
const WhatsAppConfigPage = () => {
  const auth = useAuth();
  const [config, setConfig] = useState({
    id: 'site_settings',
    whatsapp_number: '56975294340',
    whatsapp_display: '+56 9 7529 4340',
    phone_display: '+56 9 7529 4340',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!auth?.token) { setLoading(false); return; }
    adminApi(auth.token).get('/admin/site-settings')
      .then(r => { if (r.data) setConfig(prev => ({ ...prev, ...r.data })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [auth?.token]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi(auth.token).put('/admin/site-settings', config);
      toast.success('WhatsApp actualizado correctamente');
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /><span className="text-sm text-slate-400">Cargando...</span></div>;

  const testUrl = `https://wa.me/${config.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Test desde admin ALLCOM')}`;

  return (
    <div data-testid="admin-whatsapp">
      <h1 className="text-2xl font-semibold mb-2" style={{fontFamily:'Syne,sans-serif'}}>Configuración WhatsApp</h1>
      <p className="text-sm text-slate-400 mb-6">Configura el número de WhatsApp de ventas que aparece en todo el sitio.</p>

      <div className="max-w-2xl space-y-5 bg-white p-6 rounded-xl border border-slate-200 mb-6">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Número WhatsApp (sin + ni espacios)</label>
          <input value={config.whatsapp_number || ''} onChange={e => setConfig({...config, whatsapp_number: e.target.value})} className={ic} placeholder="56975294340" />
          <p className="text-[10px] text-slate-400 mt-1">Formato: código país + número sin espacios. Ej: 56975294340</p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Número para mostrar</label>
          <input value={config.whatsapp_display || ''} onChange={e => setConfig({...config, whatsapp_display: e.target.value})} className={ic} placeholder="+56 9 7529 4340" />
          <p className="text-[10px] text-slate-400 mt-1">Cómo se ve en el sitio: +56 9 7529 4340</p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Teléfono general</label>
          <input value={config.phone_display || ''} onChange={e => setConfig({...config, phone_display: e.target.value})} className={ic} placeholder="+56 9 7529 4340" />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={save} disabled={saving} className="px-6 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 disabled:opacity-50">
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
          <a href={testUrl} target="_blank" rel="noopener noreferrer" className="px-6 py-2.5 text-sm font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 inline-flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> Probar WhatsApp
          </a>
        </div>
      </div>

      {/* Preview */}
      <div className="max-w-2xl p-5 rounded-xl bg-white border border-slate-200">
        <h3 className="text-sm font-semibold mb-3">Vista previa</h3>
        <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-white" /></div>
          <div>
            <div className="text-sm font-semibold text-slate-700">WhatsApp Ventas ALLCOM</div>
            <div className="text-xs text-slate-500">{config.whatsapp_display || config.whatsapp_number}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== EMAIL CONFIG PAGE =====
const EmailConfigPage = () => {
  const auth = useAuth();
  const [config, setConfig] = useState({
    id: 'email_settings',
    from_email: 'ALLCOM <onboarding@resend.dev>',
    ventas_email: 'ventas@allcom.cl',
    contacto_email: 'contacto@allcom.cl',
    email_enabled: true,
    resend_api_key_set: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    if (!auth?.token) { setLoading(false); return; }
    adminApi(auth.token).get('/admin/email-config')
      .then(r => { if (r.data) setConfig(prev => ({ ...prev, ...r.data })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [auth?.token]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi(auth.token).put('/admin/email-config', config);
      toast.success('Configuración de email guardada');
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error('Ingresa un email de prueba'); return; }
    setTesting(true);
    try {
      const res = await adminApi(auth.token).post('/admin/test-email', { to: testEmail });
      if (res.data.success) toast.success('Email de prueba enviado correctamente');
      else toast.error(`Error: ${res.data.error}`);
    } catch (e) { toast.error('Error al enviar email de prueba'); }
    setTesting(false);
  };

  if (loading) return <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /><span className="text-sm text-slate-400">Cargando...</span></div>;

  return (
    <div data-testid="admin-email-config">
      <h1 className="text-2xl font-semibold mb-2" style={{fontFamily:'Syne,sans-serif'}}>Configuración de Email</h1>
      <p className="text-sm text-slate-400 mb-6">Configura los correos de destino y prueba el envío de notificaciones.</p>

      {/* Status */}
      <div className="flex items-center gap-3 mb-6 p-4 rounded-xl bg-white border border-slate-200">
        <div className={`w-3 h-3 rounded-full ${config.resend_api_key_set ? 'bg-emerald-500' : 'bg-red-500'}`} />
        <span className="text-sm font-medium">{config.resend_api_key_set ? 'Resend API Key configurada — Emails habilitados' : 'Resend API Key no configurada — Emails deshabilitados'}</span>
      </div>

      <div className="max-w-2xl space-y-5 bg-white p-6 rounded-xl border border-slate-200 mb-6">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email remitente (From)</label>
          <input value={config.from_email || ''} onChange={e => setConfig({...config, from_email: e.target.value})} className={ic} />
          <p className="text-[10px] text-slate-400 mt-1">Dominio verificado. Ej: ALLCOM &lt;notificaciones@allcom.cl&gt;</p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email Ventas (formularios enterprise, ROI, TCO, soluciones)</label>
          <input value={config.ventas_email || ''} onChange={e => setConfig({...config, ventas_email: e.target.value})} className={ic} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email Contacto (formularios mini, contacto general)</label>
          <input value={config.contacto_email || ''} onChange={e => setConfig({...config, contacto_email: e.target.value})} className={ic} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">CC — Copia a CEO (todos los leads)</label>
          <input value={config.cc_email || ''} onChange={e => setConfig({...config, cc_email: e.target.value})} className={ic} />
          <p className="text-[10px] text-slate-400 mt-1">Este correo recibirá copia de todos los leads. Dejar vacío para no enviar copia.</p>
        </div>
        <div className="pt-2">
          <button onClick={save} disabled={saving} className="px-6 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 disabled:opacity-50">
            {saving ? 'Guardando...' : 'Guardar configuración'}
          </button>
        </div>
      </div>

      {/* Test Email */}
      <div className="max-w-2xl p-6 rounded-xl bg-white border border-slate-200">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Send className="w-4 h-4 text-[#0EA5E9]" />Enviar email de prueba</h3>
        <div className="flex gap-3">
          <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="tu@email.cl" className={`flex-1 ${ic}`} />
          <button onClick={sendTestEmail} disabled={testing} className="px-5 py-2.5 text-sm font-semibold text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 disabled:opacity-50 whitespace-nowrap">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar prueba'}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">Envía un email de prueba para verificar que todo funciona correctamente.</p>
      </div>
    </div>
  );
};

// ===== DEMO KPI PAGE =====
const DemoKPIPage = () => {
  const auth = useAuth();
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchKpis = useCallback(() => {
    if (!auth?.token) { setLoading(false); return; }
    setLoading(true);
    adminApi(auth.token).get('/admin/demo-kpis')
      .then(r => { setKpis(r.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [auth?.token]);

  useEffect(fetchKpis, [fetchKpis]);
  const deleteKpi = async (id) => { if(!window.confirm('¿Eliminar?'))return; await adminApi(auth.token).delete(`/admin/demo-kpis/${id}`); toast.success('Eliminado'); fetchKpis(); };
  const saveKpi = async (data) => {
    try {
      if(data.id){await adminApi(auth.token).put(`/admin/demo-kpis/${data.id}`,data);}else{await adminApi(auth.token).post('/admin/demo-kpis',data);}
      toast.success('Guardado'); setEditing(null); fetchKpis();
    } catch { toast.error('Error al guardar'); }
  };
  if(editing!==null) return (
    <div>
      <button onClick={()=>setEditing(null)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft className="w-4 h-4" />Volver</button>
      <h2 className="text-xl font-semibold mb-6" style={{fontFamily:'Syne'}}>KPI Demo</h2>
      <KpiForm kpi={editing} onSave={saveKpi} />
    </div>
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="text-2xl font-semibold" style={{fontFamily:'Syne'}}>KPIs Demos ({kpis.length})</h1><button onClick={()=>setEditing({})} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600"><Plus className="w-4 h-4" />Nuevo KPI</button></div>
      <p className="text-xs text-slate-400 mb-4">KPIs personalizados para las demos de CEO Cockpit™ y C-Suite Dashboard™</p>
      {loading?<Loader2 className="w-6 h-6 animate-spin" />:(
        <div className="space-y-2">
          {kpis.map(k=>(<div key={k.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200"><div><span className="text-[10px] px-2 py-0.5 bg-sky-50 text-[#0EA5E9] rounded-full font-medium">{k.page||'ceocockpit'}</span><span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full font-medium ml-1">{k.industry||'retail'}</span><h3 className="text-sm font-semibold mt-1">{k.label}</h3><p className="text-xs text-slate-500">{k.value} {k.suffix}</p></div><div className="flex gap-1"><button onClick={()=>setEditing(k)} className="p-2 text-slate-400 hover:text-[#0EA5E9] hover:bg-sky-50 rounded-lg"><Pencil className="w-4 h-4" /></button><button onClick={()=>deleteKpi(k.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button></div></div>))}
          {kpis.length===0&&<div className="p-8 text-center text-slate-400 bg-white rounded-xl border border-slate-200">No hay KPIs personalizados. Los demos usan datos por defecto.</div>}
        </div>
      )}
    </div>
  );
};
const KpiForm = ({kpi, onSave}) => {
  const [f, setF] = useState(kpi||{});
  return (
    <form onSubmit={e=>{e.preventDefault();onSave(f);}} className="max-w-lg space-y-4 bg-white p-6 rounded-xl border border-slate-200">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-xs font-medium text-slate-500 mb-1 block">Página</label><select value={f.page||''} onChange={e=>setF({...f,page:e.target.value})} className={ic}><option value="ceocockpit">CEO Cockpit</option><option value="csuite">C-Suite</option></select></div>
        <div><label className="text-xs font-medium text-slate-500 mb-1 block">Industria</label><select value={f.industry||''} onChange={e=>setF({...f,industry:e.target.value})} className={ic}><option value="retail">Retail</option><option value="supermercados">Supermercados</option><option value="bancos">Bancos</option><option value="clinicas">Clínicas</option><option value="mineria">Minería</option><option value="gobierno">Gobierno</option><option value="aeropuerto">Aeropuerto</option></select></div>
      </div>
      <input placeholder="Etiqueta (ej: Dispositivos online)" value={f.label||''} onChange={e=>setF({...f,label:e.target.value})} className={ic} required />
      <div className="grid grid-cols-2 gap-4">
        <input placeholder="Valor (ej: 95.2)" value={f.value||''} onChange={e=>setF({...f,value:e.target.value})} className={ic} />
        <input placeholder="Sufijo (ej: %)" value={f.suffix||''} onChange={e=>setF({...f,suffix:e.target.value})} className={ic} />
      </div>
      <button type="submit" className="px-6 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600">Guardar</button>
    </form>
  );
};


// ===== ADMIN USERS PAGE =====
const AdminUsersPage = () => {
  const auth = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [changingPw, setChangingPw] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'admin' });
  const [newPw, setNewPw] = useState('');

  const fetchUsers = useCallback(() => {
    if (!auth?.token) return;
    setLoading(true);
    adminApi(auth.token).get('/admin/users').then(r => { setUsers(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, [auth?.token]);
  useEffect(fetchUsers, [fetchUsers]);

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password) { toast.error('Email y contraseña son obligatorios'); return; }
    try {
      await adminApi(auth.token).post('/admin/users', newUser);
      toast.success('Usuario creado');
      setCreating(false);
      setNewUser({ email: '', password: '', name: '', role: 'admin' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const changePassword = async (email) => {
    if (newPw.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    try {
      await adminApi(auth.token).put(`/admin/users/${email}/password`, { password: newPw });
      toast.success('Contraseña actualizada');
      setChangingPw(null);
      setNewPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`¿Eliminar usuario ${email}?`)) return;
    try {
      await adminApi(auth.token).delete(`/admin/users/${email}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) return <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /><span className="text-sm text-slate-400">Cargando usuarios...</span></div>;

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold" style={{fontFamily:'Syne,sans-serif'}}>Usuarios Admin ({users.length})</h1>
          <p className="text-sm text-slate-400 mt-1">Gestiona los usuarios que pueden acceder al panel de administración.</p>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600"><Plus className="w-4 h-4" />Nuevo usuario</button>
      </div>

      {/* Create form */}
      {creating && (
        <form onSubmit={createUser} className="mb-6 p-5 rounded-xl bg-white border border-[#0EA5E9]/30 shadow-sm space-y-3 max-w-lg">
          <h3 className="text-sm font-semibold">Crear nuevo usuario</h3>
          <input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} placeholder="Nombre completo" className={ic} />
          <input value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} placeholder="Email" type="email" required className={ic} />
          <input value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} placeholder="Contraseña (mín 6 caracteres)" type="password" required className={ic} />
          <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})} className={ic}>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
            <option value="editor">Editor</option>
            <option value="viewer">Solo lectura</option>
          </select>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600">Crear usuario</button>
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 text-sm font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50">Cancelar</button>
          </div>
        </form>
      )}

      {/* Users list */}
      <div className="space-y-3">
        {users.map(u => (
          <div key={u.email} className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#0A2540] flex items-center justify-center text-white text-sm font-bold">
                  {(u.name || u.email).charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">{u.name || u.email}</h3>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${u.role === 'super_admin' ? 'bg-violet-100 text-violet-600' : u.role === 'editor' ? 'bg-amber-100 text-amber-600' : u.role === 'viewer' ? 'bg-slate-100 text-slate-500' : 'bg-sky-100 text-sky-600'}`}>
                      {u.role || 'admin'}
                    </span>
                    {u.email === auth?.email && <span className="text-[9px] px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded-full font-semibold">Tú</span>}
                  </div>
                  <p className="text-xs text-slate-400">{u.email}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setChangingPw(u.email); setNewPw(''); }} className="flex items-center gap-1 p-2 text-xs text-slate-400 hover:text-[#0EA5E9] hover:bg-sky-50 rounded-lg" title="Cambiar contraseña">
                  <Key className="w-3.5 h-3.5" />
                </button>
                {u.email !== auth?.email && (
                  <button onClick={() => deleteUser(u.email)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
            {/* Change password form */}
            {changingPw === u.email && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2 items-center">
                <Key className="w-4 h-4 text-slate-400 shrink-0" />
                <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Nueva contraseña (mín 6 caracteres)" className={ic + ' flex-1'} />
                <button onClick={() => changePassword(u.email)} className="px-3 py-2 text-xs font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600 whitespace-nowrap">Cambiar</button>
                <button onClick={() => setChangingPw(null)} className="px-3 py-2 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50">Cancelar</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== MAIN ADMIN APP =====
export default function AdminPanel() {
  const [auth, setAuth] = useState(() => {
    const saved = localStorage.getItem('allcom_admin');
    return saved ? JSON.parse(saved) : null;
  });
  const location = useLocation();
  const login = (data) => { localStorage.setItem('allcom_admin', JSON.stringify(data)); setAuth(data); };
  const logout = () => { localStorage.removeItem('allcom_admin'); setAuth(null); };

  // Validate token on load and handle 401 globally
  useEffect(() => {
    if (!auth?.token) return;
    adminApi(auth.token).get('/admin/me').catch(err => {
      if (err?.response?.status === 401) {
        toast.error('Sesión expirada. Inicia sesión nuevamente.');
        logout();
      }
    });
  }, [auth?.token]);

  // Global 401 interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      r => r,
      err => {
        if (err?.response?.status === 401 && window.location.pathname.startsWith('/admin')) {
          localStorage.removeItem('allcom_admin');
          setAuth(null);
          toast.error('Sesión expirada');
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  if (!auth) return <LoginPage onLogin={login} />;
  const authValue = { ...auth, logout };
  const getActive = () => {
    const p = location.pathname;
    if (p === '/admin') return 'dashboard';
    if (p.includes('/leads')) return 'leads';
    if (p.includes('/team')) return 'team';
    if (p.includes('/products')) return 'products';
    if (p.includes('/hw-categories')) return 'hw-categories';
    if (p.includes('/solutions')) return 'solutions';
    if (p.includes('/cases')) return 'cases';
    if (p.includes('/crew')) return 'crew';
    if (p.includes('/kian-news')) return 'kian-news';
    if (p.includes('/newsletter')) return 'newsletter';
    if (p.includes('/site-config')) return 'site-config';
    if (p.includes('/content')) return 'content';
    if (p.includes('/whatsapp')) return 'whatsapp';
    if (p.includes('/email-config')) return 'email-config';
    if (p.includes('/demo-kpis')) return 'demo-kpis';
    if (p.includes('/menu-builder')) return 'menu-builder';
    if (p.includes('/users')) return 'admin-users';
    return 'dashboard';
  };
  return (
    <AuthContext.Provider value={authValue}>
      <Routes>
        <Route path="/" element={<AdminLayout active={getActive()}><DashboardPage /></AdminLayout>} />
        <Route path="/leads" element={<AdminLayout active="leads"><LeadsPage /></AdminLayout>} />
        <Route path="/team" element={<AdminLayout active="team"><TeamPage /></AdminLayout>} />
        <Route path="/products" element={<AdminLayout active="products"><CrudPage title="Productos" collection="products" apiPath="products" fields={productFields} categories={hwCategories} /></AdminLayout>} />
        <Route path="/hw-categories" element={<AdminLayout active="hw-categories"><CrudPage title="Categorías Hardware" collection="hw-categories" apiPath="hardware/categories" fields={hwCategoryFields} /></AdminLayout>} />
        <Route path="/solutions" element={<AdminLayout active="solutions"><CrudPage title="Soluciones" collection="solutions" apiPath="solutions" fields={solutionFields} /></AdminLayout>} />
        <Route path="/menu-builder" element={<AdminLayout active="menu-builder"><MenuBuilder /></AdminLayout>} />
        <Route path="/cases" element={<AdminLayout active="cases"><CrudPage title="Casos" collection="cases" apiPath="cases" fields={caseFields} /></AdminLayout>} />
        <Route path="/crew" element={<AdminLayout active="crew"><CrudPage title="Crew / Equipo Web" collection="crew" apiPath="public/crew" fields={crewFields} /></AdminLayout>} />
        <Route path="/releases" element={<AdminLayout active="releases"><CrudPage title="Releases (Novedades)" collection="releases" apiPath="releases" fields={releaseFields} /></AdminLayout>} />
        <Route path="/kian-news" element={<AdminLayout active="kian-news"><KianNewsAdmin token={auth.token} /></AdminLayout>} />
        <Route path="/newsletter" element={<AdminLayout active="newsletter"><NewsletterAdmin token={auth.token} /></AdminLayout>} />
        <Route path="/site-config" element={<AdminLayout active="site-config"><SiteConfigPage /></AdminLayout>} />
        <Route path="/content" element={<AdminLayout active="content"><ContentManagerPage /></AdminLayout>} />
        <Route path="/whatsapp" element={<AdminLayout active="whatsapp"><WhatsAppConfigPage /></AdminLayout>} />
        <Route path="/email-config" element={<AdminLayout active="email-config"><EmailConfigPage /></AdminLayout>} />
        <Route path="/demo-kpis" element={<AdminLayout active="demo-kpis"><DemoKPIPage /></AdminLayout>} />
        <Route path="/users" element={<AdminLayout active="admin-users"><AdminUsersPage /></AdminLayout>} />
      </Routes>
    </AuthContext.Provider>
  );
}
